import { Component, inject, OnInit } from "@angular/core";
import { NzMessageService } from "ng-zorro-antd/message";
import {
  Ec2InstancesService,
  EC2InstanceItem
} from "../services/ec2-instances.service";
import { CachedUserService } from "../../shared/services/cached-user.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { take } from "rxjs";

@Component({
  selector: "app-ec2-instances-page",
  templateUrl: "./ec2-instances-page.component.html",
  styleUrls: ["./ec2-instances-page.component.less"],
  standalone: false,
})
export class Ec2InstancesPageComponent implements OnInit {
  isLoading = false;
  instances: EC2InstanceItem[] = [];
  addModalVisible = false;

  addForm: FormGroup;

  actionLoading: { [instanceId: string]: boolean } = {};
  isComputeAdmin = false;
  allUsers: { id: string; email: string }[] = [];
  manageUsersVisible = false;
  managingInstance: EC2InstanceItem | null = null;
  instanceUsersLoading = false;
  selectedUserIds: string[] = [];

  private _fb = inject(FormBuilder);

  constructor(
    private ec2Service: Ec2InstancesService,
    private message: NzMessageService,
    private cachedUserService: CachedUserService,
  ) {
    this.addForm = this._fb.group({
      id: "",
      region: ["us-ease-1", Validators.required],
      name: ["General Description", Validators.required],
      descriptipon: "",
    });
  }

  ngOnInit(): void {
    this.cachedUserService.getUserDetails().pipe(take(1)).subscribe({
      next: (detail) => {
        this.isComputeAdmin = !!(detail && detail.isComputeAdmin);
        this.allUsers = detail?.all_users || [];
        this.loadInstances();
      },
      error: () => this.loadInstances(),
    });
  }

  loadInstances(): void {
    this.isLoading = true;
    this.ec2Service.list().pipe(take(1)).subscribe({
      next: (data) => {
        this.instances = Array.isArray(data) ? data : [];
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.message.error("Failed to load EC2 instances.");
        this.isLoading = false;
      },
    });
  }

  openAddModal(): void {
    this.addForm.reset();
    this.addModalVisible = true;
  }

  closeAddModal(): void {
    this.addModalVisible = false;
  }

  submitAdd(): void {
    if (!this.addForm.valid) return
    
    const values = this.addForm.value;
    console.log("values submitting", values);
    if (!values.id?.trim()) {
      this.message.warning("Instance ID is required.");
      return;
    }
    this.isLoading = true;
    this.ec2Service.create(values).pipe(take(1)).subscribe(
      {next: () => {
        this.message.success("Instance added successfully.");
        this.closeAddModal();
        this.loadInstances();
      },
      error: (err) => {
        this.isLoading = false;
        const msg =
          err?.error?.error || err?.message || "Failed to add instance.";
        this.message.error(msg);
      },}
    );
  }

  changeState(
    instance: EC2InstanceItem,
    action: "start" | "stop" | "reboot",
  ): void {
    this.actionLoading[instance.id] = true;
    this.ec2Service.changeState(instance.id, action).pipe(take(1)).subscribe({
      next: (res) => {
        this.actionLoading[instance.id] = false;
        instance.state = res.state;
        this.message.success(`Instance ${action} initiated.`);
      },
      error: (err) => {
        this.actionLoading[instance.id] = false;
        const msg = err?.error?.error || err?.message || "Action failed.";
        this.message.error(msg);
      },
    });
  }

  canStart(instance: EC2InstanceItem): boolean {
    return instance.state === "stopped";
  }

  canStop(instance: EC2InstanceItem): boolean {
    return instance.state === "running";
  }

  canReboot(instance: EC2InstanceItem): boolean {
    return instance.state === "running";
  }

  openManageUsers(instance: EC2InstanceItem): void {
    if (!this.isComputeAdmin) return;
    this.managingInstance = instance;
    this.manageUsersVisible = true;
    this.instanceUsersLoading = true;
    this.selectedUserIds = [];

    this.ec2Service.listInstanceUsers(instance.id).pipe(take(1)).subscribe({
      next: (users) => {
        this.instanceUsersLoading = false;
        this.selectedUserIds = (users || []).map((u) => u.id);
      },
      error: () => {
        this.instanceUsersLoading = false;
        this.message.error("Failed to load instance users.");
      },
    });
  }

  closeManageUsers(): void {
    this.manageUsersVisible = false;
    this.managingInstance = null;
  }

  saveManageUsers(): void {
    if (!this.managingInstance) return;
    this.instanceUsersLoading = true;
    this.ec2Service
      .updateInstanceUsers(this.managingInstance.id, this.selectedUserIds)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.instanceUsersLoading = false;
          this.message.success("Instance users updated.");
          this.closeManageUsers();
          this.loadInstances();
        },
        error: () => {
          this.instanceUsersLoading = false;
          this.message.error("Failed to update instance users.");
        },
      });
  }

  deleteInstance(instance: EC2InstanceItem): void {
    if (!this.isComputeAdmin) return;
    this.actionLoading[instance.id] = true;
    this.ec2Service.delete(instance.id).pipe(take(1)).subscribe({
      next: () => {
        this.actionLoading[instance.id] = false;
        this.message.success("Instance deleted.");
        this.loadInstances();
      },
      error: (err) => {
        this.actionLoading[instance.id] = false;
        const msg =
          err?.error?.error ||
          err?.error?.detail ||
          err?.message ||
          "Failed to delete instance.";
        this.message.error(msg);
      },
    });
  }
}
